import { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { useClinicalTrialInventorySlice } from '@restore/clinicalTrialInventory/clinicalTrialInventory.slice';
import { selectProps, inputProps } from '../formProps';
import {
  propertyOperatorOptions,
  tagConditionScopeOptions, taskStatusesOptions,
  taskTagType,
} from '../TaskCondinions/conditionOptions';
import { TaskStatuses } from '@dynamika-shared/types/pmTaskDefinitions';
import { twMerge } from '@utils/twMerge';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { TrashIcon } from '@dynamika-shared/ui/icons';
import { sortOptionsByLabel } from 'src/utils/sortOptions';

type AttachToParentTaskProps = {
  index: number;
};

export const AttachToParentTask: FC<AttachToParentTaskProps> = ({ index }) => {
  const { setValue, watch } = useFormContext();
  const tags = useClinicalTrialInventorySlice(state => state.tags);

  const parentTask = watch(`actions.actions[${index}].parentTask`);

  const optionTags = useMemo(
    () => tags.map(({ id, name }) => ({ value: id, label: name })).sort(sortOptionsByLabel),
    [tags]
  );

  const setScope = (scope: string) => {
    setValue(`actions.actions[${index}].parentTask`, {
      ...parentTask,
      scope,
    });
  };

  const setCollectionElementType = (type: string) => {
    const data =
      type === '.TagIdStatuses'
        ? {
            type,
            statuses: [],
            id: parentTask?.collectionElementType?.id ?? '',
          }
        : type === '.TagIdResultProperty'
        ? {
            type,
            id: parentTask?.collectionElementType?.id ?? '',
            status:
              parentTask?.collectionElementType?.status ??
              TaskStatuses.COMPLETED,
            propertyOperator: '=',
          }
        : {
            type,
            id: parentTask?.collectionElementType?.id ?? '',
            status:
              parentTask?.collectionElementType?.status ??
              TaskStatuses.COMPLETED,
          };
    setValue(
      `actions.actions[${index}].parentTask.collectionElementType`,
      data
    );
  };

  const setTagId = (id: string) => {
    setValue(
      `actions.actions[${index}].parentTask.collectionElementType.id`,
      id
    );
  };

  const setStatus = (status: string) => {
    setValue(
      `actions.actions[${index}].parentTask.collectionElementType.status`,
      status
    );
  };

  const onSelectStatus = (status: TaskStatuses) => {
    const currentStatuses = parentTask?.collectionElementType?.statuses ?? [];
    setValue(
      `actions.actions[${index}].parentTask.collectionElementType.statuses`,
      [...currentStatuses, status]
    );
  };

  const onDeselectStatus = (status: TaskStatuses) => {
    const currentStatuses = parentTask?.collectionElementType?.statuses ?? [];
    setValue(
      `actions.actions[${index}].parentTask.collectionElementType.statuses`,
      currentStatuses.filter((s: TaskStatuses) => s !== status)
    );
  };

  const setPropertyPath = (path: string) => {
    setValue(
      `actions.actions[${index}].parentTask.collectionElementType.propertyPath`,
      path
    );
  };

  const setPropertyOperator = (operator: string) => {
    setValue(
      `actions.actions[${index}].parentTask.collectionElementType.propertyOperator`,
      operator
    );
  };

  const setPropertyValue = (value: string) => {
    setValue(
      `actions.actions[${index}].parentTask.collectionElementType.propertyValue`,
      value
    );
  };

  const deleteParentTask = () => {
    setValue(`actions.actions[${index}].parentTask`, null);
  };

  if (!parentTask) {
    return (
      <div className='mt-2'>
        <Button
          type={'text'}
          className='text-1424 text-vividTangelo font-medium'
          onClick={() =>
            setValue(`actions.actions[${index}].parentTask`, {
              scope: '',
              collectionElementType: { type: '', id: '' },
            })
          }
        >
          + Attach to Parent Task
        </Button>
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        ' relative w-full bg-body p-2 pt-0 rounded-md'
      )}
    >
      <h2 className={'mt-2 text-1624 font-medium text-vividTangelo'}>Parent task</h2>
      <div className={'flex justify-between items-start gap-3 w-full'}>
        <div className={'flex gap-3 justify-between'}>
          <div className={'flex gap-3 flex-wrap'}>
            <Select
              {...selectProps}
              value={parentTask?.scope}
              options={tagConditionScopeOptions}
              onChange={setScope}
              label={'Focus'}
              placeholder={'Select focus'}
              listHeight={280}
            />
            <Select
              {...selectProps}
              options={optionTags}
              value={parentTask?.collectionElementType?.id}
              onChange={setTagId}
              label='Tag'
              placeholder='Select tag'
              autoDropdownWidth
            />
            <Select
              {...selectProps}
              value={parentTask?.collectionElementType?.type}
              options={taskTagType}
              onChange={setCollectionElementType}
              label={'Filter'}
              placeholder={'Select filter'}
            />
            {parentTask?.collectionElementType?.type === '.TagIdStatuses' ? (
              <Select
                {...selectProps}
                mode='multiple'
                maxTagCount={1}
                options={taskStatusesOptions}
                value={parentTask?.collectionElementType?.statuses}
                onSelect={onSelectStatus}
                onDeselect={onDeselectStatus}
                label='Statuses'
                placeholder='Select statuses'
              />
            ) : (
              <Select
                {...selectProps}
                options={taskStatusesOptions}
                value={parentTask?.collectionElementType?.status}
                onChange={setStatus}
                label='Status'
                placeholder='Select status'
              />
            )}
            {parentTask?.collectionElementType?.type ===
              '.TagIdResultProperty' && (
              <div className='flex items-end'>
                <Input
                  {...inputProps}
                  className='-ml-[1.15rem] mr-1.5 w-[140px]'
                  label='Result Path'
                  placeholder='Path'
                  value={parentTask?.collectionElementType?.propertyPath}
                  onChange={e => setPropertyPath(e.target.value)}
                />
                <Select
                  {...selectProps}
                  className='mr-1.5 w-[80px]'
                  options={propertyOperatorOptions}
                  value={parentTask?.collectionElementType?.propertyOperator || '='}
                  onChange={setPropertyOperator}
                  label='Operator'
                  placeholder='='
                />
                <Input
                  {...inputProps}
                  label='Result Value'
                  placeholder='Value'
                  value={parentTask?.collectionElementType?.propertyValue}
                  onChange={e => setPropertyValue(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
        <TrashIcon
          className='cursor-pointer hover:text-redOrange mt-3 min-w-3'
          width={24}
          height={24}
          onClick={deleteParentTask}
        />
      </div>

    </div>
  );
};
